import { useMemo, useState } from 'react'

import { match, P } from 'ts-pattern'

import { Currency } from '@sequencehq/api'
import {
  Box,
  Button,
  Card,
  Flex,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  IconPlus,
  Primary12Regular,
  Primary13SemiBold,
  PriceInput,
  RedRed60,
  Section,
  SequenceMenu,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@sequencehq/core-components'
import { localDateWithFormat, toMoney } from '@sequencehq/utils'

import { DATE_FORMAT_DISPLAY } from 'Invoices/constants'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import { InvoiceListItem } from 'Invoices/types'

import { InvoiceSearchSelect } from './InvoiceSearchSelect'
import {
  ApplyCreditRow,
  ApplyTotals,
  getRowAmountFeedback,
  remainingCreditForRow,
  toIneligibilityMessages
} from './applyCreditNote.helpers'

type Props = {
  rows: ApplyCreditRow[]
  customerId: string
  currency: Currency
  /** The credit note's applicable balance, the ceiling across all rows. */
  creditNoteBalance: string
  totals: ApplyTotals
  onChangeAmount: (rowId: string, amount: string) => void
  onRemoveRow: (rowId: string) => void
  onSelectInvoice: (invoice: InvoiceListItem) => void
}

/**
 * The invoices a credit note's balance is being applied to: one row per
 * invoice with its outstanding amount and the credit to apply, plus the
 * picker that adds them.
 */
export const ApplyCreditsToSection = ({
  rows,
  customerId,
  currency,
  creditNoteBalance,
  totals,
  onChangeAmount,
  onRemoveRow,
  onSelectInvoice
}: Props) => {
  // The picker always lives behind "Add invoice", including the first time.
  const [isPickerRequested, setIsPickerRequested] = useState(false)

  // Stable identity so the picker's option mapper isn't rebuilt every render.
  const addedInvoiceIds = useMemo(() => rows.map(row => row.invoiceId), [rows])

  const handleSelectInvoice = (invoice: InvoiceListItem) => {
    setIsPickerRequested(false)
    onSelectInvoice(invoice)
  }

  return (
    // marginBottom is reset to 0 because the parent owns inter-section spacing.
    <Section title="Apply credits to" marginBottom={0}>
      {/* Rendered from the start, so the drawer opens on the shape of what is
          being built. With no rows the add-invoice row is the only row. */}
      <Card variant="v2" padding="0px">
        <Table
          variant="v2-without-hover"
          width="100%"
          data-testid="creditNote.applyCredits.table"
        >
          <Thead>
            <Tr>
              <Th>Invoice</Th>
              <Th width={AMOUNT_DUE_WIDTH} textAlign="right">
                Amount due
              </Th>
              <Th width={CREDIT_APPLIED_WIDTH} textAlign="right">
                Credit applied
              </Th>
              <Th width={KEBAB_WIDTH} />
            </Tr>
          </Thead>
          <Tbody>
            {rows.map(row => (
              <InvoiceRow
                key={row.rowId}
                row={row}
                currency={currency}
                remainingCredit={remainingCreditForRow({
                  creditNoteBalance,
                  totals,
                  rowId: row.rowId
                })}
                onChangeAmount={amount => onChangeAmount(row.rowId, amount)}
                onRemove={() => onRemoveRow(row.rowId)}
              />
            ))}
            {/* Last row of the table, mirroring LineItemGroup.AddLineItem: the
                picker takes its place while an invoice is being chosen. It fills
                the invoice column only, stopping short of the amount columns. */}
            <Tr borderBottom="none !important">
              <Td colSpan={PICKER_COLUMN_SPAN}>
                {isPickerRequested ? (
                  <InvoiceSearchSelect
                    customerId={customerId}
                    currency={currency}
                    addedInvoiceIds={addedInvoiceIds}
                    onSelect={handleSelectInvoice}
                    onDismiss={() => setIsPickerRequested(false)}
                  />
                ) : (
                  <Button
                    variant="secondary"
                    leadingIcon={<IconPlus />}
                    onClick={() => setIsPickerRequested(true)}
                    data-testid="creditNote.applyCredits.addInvoice"
                  >
                    Add invoice
                  </Button>
                )}
              </Td>
              <Td colSpan={COLUMN_COUNT - PICKER_COLUMN_SPAN} />
            </Tr>
          </Tbody>
        </Table>
      </Card>
    </Section>
  )
}

// Matches LineItemGroup's read-only rate and editable credit amount columns.
const AMOUNT_DUE_WIDTH = '124px'
const CREDIT_APPLIED_WIDTH = '128px'
// Matches LineItemGroup's kebab column.
const KEBAB_WIDTH = '56px'
const COLUMN_COUNT = 4
// The invoice column, the width the invoice picker is allowed to fill.
const PICKER_COLUMN_SPAN = 1

const InvoiceRow = ({
  row,
  currency,
  remainingCredit,
  onChangeAmount,
  onRemove
}: {
  row: ApplyCreditRow
  currency: Currency
  remainingCredit: string
  onChangeAmount: (amount: string) => void
  onRemove: () => void
}) => {
  const formatDueDate = useInvoiceDueDateFormatter()

  const feedback = getRowAmountFeedback(
    row.amount,
    row.invoiceBalance,
    remainingCredit
  )

  const amountError = match(feedback)
    .with({ type: 'empty' }, () => 'Enter an amount')
    .with({ type: 'nonPositive' }, () => 'Enter an amount greater than zero')
    .with(
      { type: 'exceedsInvoice' },
      ({ invoiceBalance }) =>
        `Amount cannot exceed the invoice balance of ${toMoney({
          value: invoiceBalance,
          currency
        })}`
    )
    .with(
      { type: 'exceedsCredit' },
      ({ remainingCredit: remaining }) =>
        `Amount cannot exceed the remaining credit of ${toMoney({
          value: remaining,
          currency
        })}`
    )
    .otherwise(() => null)

  const ineligibilityMessages = match(row.eligibility)
    .with({ ineligible: P.array() }, ({ ineligible }) =>
      toIneligibilityMessages(ineligible)
    )
    .otherwise(() => [])

  const hasMessages = Boolean(amountError) || ineligibilityMessages.length > 0

  const dueDateText = formatDueDate({
    dueDate: row.dueDate,
    formatDate: dueDate => localDateWithFormat(dueDate, DATE_FORMAT_DISPLAY),
    prefix: 'Due '
  })

  return (
    <>
      {/* The messages row below carries the divider instead, so the two read as
          one block. !important beats the variant's `tbody tr` selector. */}
      <Tr {...(hasMessages ? { borderBottom: 'none !important' } : {})}>
        <Td>
          <Flex flexDir="column" gap="4px">
            <Box {...Primary13SemiBold} color={GreyGrey90}>
              {row.invoiceNumber}
            </Box>
            {dueDateText && (
              <Box
                {...Primary12Regular}
                color={GreyGrey60}
                data-testid={`creditNote.applyCredits.row.${row.invoiceId}.dueDate`}
              >
                {dueDateText}
              </Box>
            )}
          </Flex>
        </Td>
        <Td textAlign="right" color={GreyGrey70}>
          {toMoney({ value: row.invoiceBalance, currency })}
        </Td>
        {/* The bare input, not PriceField: the error renders in the row below so
            it has the full table width rather than this narrow column. */}
        <Td>
          <PriceInput
            variant="v2"
            currency={currency}
            value={row.amount}
            isInvalid={Boolean(amountError)}
            onChange={valueAsString => onChangeAmount(valueAsString)}
            data-testid={`creditNote.applyCredits.row.${row.invoiceId}.amount`}
            // Matches the credit amount input in the issue credit note drawer.
            height="28px"
            textAlign="right"
            paddingRight="8px"
            alignItems="center"
          />
        </Td>

        <Td>
          <SequenceMenu
            variant="ghost"
            alignment="bottom-right"
            closeMenuOnItemClick
            aria-label="Invoice actions"
            data-testid={`creditNote.applyCredits.row.${row.invoiceId}.kebab`}
            items={
              <SequenceMenu.Item
                uuid="remove-invoice"
                label="Remove invoice"
                destructive
                onClick={onRemove}
                data-testid={`creditNote.applyCredits.row.${row.invoiceId}.remove`}
              />
            }
          />
        </Td>
      </Tr>
      {hasMessages && (
        <Tr>
          <Td colSpan={COLUMN_COUNT} paddingTop="0px !important">
            {/* whiteSpace resets the variant's nowrap so long errors wrap. */}
            <Flex flexDir="column" gap="4px" whiteSpace="normal">
              {amountError && (
                <Box
                  {...Primary12Regular}
                  color={RedRed60}
                  data-testid={`creditNote.applyCredits.row.${row.invoiceId}.amount.error`}
                >
                  {amountError}
                </Box>
              )}
              {ineligibilityMessages.map(({ key, message }) => (
                <Box
                  key={key}
                  {...Primary12Regular}
                  color={RedRed60}
                  data-testid={`creditNote.applyCredits.row.${row.invoiceId}.ineligible.${key}`}
                >
                  {message}
                </Box>
              ))}
            </Flex>
          </Td>
        </Tr>
      )}
    </>
  )
}
